import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {SyncingLine} from '@dropbox/dig-icons/assets';
import {useMutation, useQuery} from '@tanstack/react-query';
import {
  AiWorkstreamOutcomesValidationService,
  type WorkstreamOutcomesValidationResponse,
} from 'client';
import {t} from 'i18next';
import {useCallback, useEffect, useState} from 'react';
import {getService} from 'utilities';
import {queryClient} from 'views/QueryClientWrapper';
import {
  DraftMetric,
  getMetricOutcomesValidationQueryKey,
  getOutcomeId,
  getOutcomesValidationChecksToDisplay,
  getOutcomeTitleText,
  getWorkstreamCheckIdsForType,
  shouldShowOutcomesValidation,
  type WorkstreamCheckResponseWithSource,
} from 'views/workstreams/workstreamChecks';
import {HealthScoreResults} from 'views/workstreams/WorkstreamHealthScore';

import styles from './WorkstreamHealthScore.module.css';

export const useWorkstreamOutcomesValidation = ({
  workstreamId,
  metricId,
  enabled,
}: {
  workstreamId: number;
  metricId: string;
  enabled: boolean;
}) => {
  const {data, isLoading} = useQuery({
    queryKey: getMetricOutcomesValidationQueryKey(metricId),
    queryFn: ({signal}) => {
      const request = getService(
        AiWorkstreamOutcomesValidationService
      ).getMetricOutcomesValidationApiV1WorkstreamMetricsWorkstreamMetricIdOutcomesValidationGet(
        Number(metricId)
      );
      signal.addEventListener('abort', () => {
        request.cancel();
      });
      return request;
    },
    enabled:
      workstreamId > 0 && Number.isInteger(Number(metricId)) && Number(metricId) > 0 && enabled,
    retry: false,
    refetchOnMount: false,
  });
  return {outcomesValidation: data?.task_id ? data : null, isLoading};
};

export const useMetricOutcomesValidationGenerate = ({
  workstreamId,
  metricId,
  metricTitle,
}: {
  workstreamId: number;
  metricId: string;
  metricTitle: string;
}) => {
  const [generateTaskId, setGenerateTaskId] = useState<string | undefined>(undefined);
  const [isGenerationSkipped, setIsGenerationSkipped] = useState(false);

  const {mutateAsync: generateApi} = useMutation({
    mutationFn: () =>
      getService(
        AiWorkstreamOutcomesValidationService
      ).generateMetricOutcomesValidationApiV1WorkstreamMetricsWorkstreamMetricIdOutcomesValidationGeneratePost(
        Number(metricId)
      ),
  });

  const isGenerating = !!generateTaskId;

  const {data: taskStatus} = useQuery({
    queryKey: ['workstream', 'outcomes_validation', generateTaskId, workstreamId, metricId],
    enabled: isGenerating,
    refetchInterval: 1000,
    queryFn: () =>
      getService(
        AiWorkstreamOutcomesValidationService
      ).getMetricOutcomesValidationStatusApiV1WorkstreamMetricsWorkstreamMetricIdOutcomesValidationGenerateTaskIdGet(
        Number(metricId),
        generateTaskId as string
      ),
  });

  const {outcomesValidation} = useWorkstreamOutcomesValidation({
    workstreamId,
    metricId,
    enabled: isGenerating,
  });

  useEffect(() => {
    setIsGenerationSkipped(false);
  }, [metricId, metricTitle]);

  useEffect(() => {
    if (!generateTaskId || !taskStatus?.status || taskStatus.status === 'processing') return;

    if (taskStatus.status === 'completed') {
      // Optimistically update the outcomes validation data
      const queryKey = getMetricOutcomesValidationQueryKey(metricId);
      if (taskStatus.result_json) {
        queryClient.setQueryData(queryKey, {
          ...taskStatus.result_json,
          task_id: taskStatus.result_json.task_id ?? generateTaskId,
          metric_id: taskStatus.result_json.metric_id ?? metricId,
          metric_title: taskStatus.result_json.metric_title || metricTitle,
        });
        setGenerateTaskId(undefined);
        return;
      }

      queryClient.invalidateQueries({queryKey});
      if (outcomesValidation?.task_id === generateTaskId) {
        setGenerateTaskId(undefined);
      }
      return;
    }

    if (taskStatus.status === 'skipped') {
      setIsGenerationSkipped(true);
    }
    setGenerateTaskId(undefined); // Clear task ID to stop polling when status is received
  }, [
    generateTaskId,
    metricId,
    metricTitle,
    outcomesValidation?.task_id,
    taskStatus,
    workstreamId,
  ]);

  const generate = useCallback(async () => {
    setIsGenerationSkipped(false);
    try {
      const response = await generateApi();
      setGenerateTaskId(response.task_id);
    } catch {
      setGenerateTaskId(undefined);
    }
  }, [generateApi]);

  return {
    generateTaskId,
    generatedOutcomesValidation: outcomesValidation,
    isGenerationSkipped,
    isGenerating,
    generate,
  };
};

const WorkstreamOutcomesValidationRefreshingIcon = () => {
  return (
    <Box marginTop="2" marginRight="4">
      <UIIcon
        src={SyncingLine}
        size="small"
        aria-label="Refreshing check"
        className={styles.refreshingIcon}
      />
    </Box>
  );
};

const WorkstreamOutcomesValidationPlaceholder = () => {
  return (
    <Box
      aria-hidden
      data-testid="outcomes-validation-placeholder"
      marginTop="2"
      style={{width: 21, height: 20}}
    />
  );
};

const OutcomesValidationResults = ({
  workstreamId,
  checks,
}: {
  workstreamId: number;
  checks: WorkstreamCheckResponseWithSource[];
}) => {
  const passingChecks = checks.filter((check) => check.passed);
  const isCriteriaMet = passingChecks.length === checks.length;
  const score = getScore(passingChecks.length, checks.length);
  const scoreText = getScoreText(score);
  return (
    checks.length > 0 && (
      <HealthScoreResults
        workstreamId={workstreamId}
        score={score}
        checks={checks}
        iconSize={20}
        textOverride={scoreText}
        contentWidth={isCriteriaMet ? '400px' : '475px'}
        style={{marginTop: '2px'}}
        showCheckRationale
        source="outcomes_validation"
      />
    )
  );
};

export const DeliverableOutcomesValidationChecks = ({workstreamId}: {workstreamId: number}) => {
  const passingDeliverableOutcomesValidationChecks = getWorkstreamCheckIdsForType(
    'outcomes_validation_outcomes'
  ).map((id) => ({
    id,
    passed: true,
    rationale: null,
  }));
  return (
    <OutcomesValidationResults
      workstreamId={workstreamId}
      checks={passingDeliverableOutcomesValidationChecks}
    />
  );
};

export const WorkstreamOutcomesValidationChecks = ({
  activeTab,
  workstreamId,
  validationMetric,
  isGeneratingPreview = false,
  outcomesValidationPreview,
}: {
  activeTab: 'metrics' | 'outcomes';
  workstreamId: number;
  validationMetric: DraftMetric;
  isGeneratingPreview?: boolean;
  outcomesValidationPreview?: WorkstreamOutcomesValidationResponse | null;
}) => {
  const metricId = getOutcomeId(validationMetric);
  const showOutcomesValidation = shouldShowOutcomesValidation(validationMetric);
  const isMetricSavedToWorkstream = !validationMetric.isNew && !validationMetric.isPendingAttach;

  const shouldFetchAndShowOutcomesValidation =
    workstreamId > 0 &&
    !!metricId &&
    activeTab === 'outcomes' &&
    showOutcomesValidation &&
    isMetricSavedToWorkstream;
  const metricTitle = getOutcomeTitleText(validationMetric);
  const {outcomesValidation, isLoading} = useWorkstreamOutcomesValidation({
    workstreamId,
    metricId,
    enabled: shouldFetchAndShowOutcomesValidation,
  });
  const {generatedOutcomesValidation, generate, isGenerating, isGenerationSkipped} =
    useMetricOutcomesValidationGenerate({
      workstreamId,
      metricId,
      metricTitle,
    });
  const savedOutcomesValidation = outcomesValidation ?? generatedOutcomesValidation;

  // If we fetch outcomes validation and no result exists, generate it
  useEffect(() => {
    if (
      !shouldFetchAndShowOutcomesValidation ||
      isLoading ||
      isGenerationSkipped ||
      savedOutcomesValidation?.task_id ||
      !metricTitle
    ) {
      return;
    }

    void generate();
  }, [
    generate,
    isLoading,
    metricTitle,
    savedOutcomesValidation,
    shouldFetchAndShowOutcomesValidation,
    isGenerationSkipped,
  ]);

  const outcomesValidationToDisplay = outcomesValidationPreview ?? savedOutcomesValidation;
  const hasOutcomesValidationPreviewToDisplay = Boolean(
    outcomesValidationPreview?.task_id && outcomesValidationPreview.checks?.length
  );
  const hasOutcomesValidationToDisplay = Boolean(
    outcomesValidationToDisplay?.task_id && outcomesValidationToDisplay.checks?.length
  );
  const shouldShowOutcomesValidationPlaceholder =
    showOutcomesValidation &&
    activeTab === 'outcomes' &&
    validationMetric.type === 'milestone' &&
    !hasOutcomesValidationToDisplay;
  if (isGenerationSkipped) {
    return shouldShowOutcomesValidationPlaceholder ? (
      <WorkstreamOutcomesValidationPlaceholder />
    ) : null;
  }

  if (shouldFetchAndShowOutcomesValidation) {
    if (!metricTitle) {
      return null;
    }

    const hasNoChecksInDB = isMetricSavedToWorkstream && !savedOutcomesValidation?.checks?.length;
    if (isLoading || isGenerating || hasNoChecksInDB) {
      return <WorkstreamOutcomesValidationRefreshingIcon />;
    }
  }

  if (!showOutcomesValidation) {
    return null;
  }

  if (activeTab === 'outcomes' && isGeneratingPreview && !hasOutcomesValidationPreviewToDisplay) {
    return <WorkstreamOutcomesValidationRefreshingIcon />;
  }

  if (shouldShowOutcomesValidationPlaceholder && !isMetricSavedToWorkstream) {
    return <WorkstreamOutcomesValidationPlaceholder />;
  }

  const checksType =
    activeTab === 'metrics' ? 'outcomes_validation_metrics' : 'outcomes_validation_outcomes';
  const checks = getOutcomesValidationChecksToDisplay(
    [validationMetric],
    hasOutcomesValidationToDisplay ? (outcomesValidationToDisplay?.checks ?? []) : [],
    checksType
  );
  if (!checks) {
    return null;
  }

  return <OutcomesValidationResults workstreamId={workstreamId} checks={checks} />;
};

const getScore = (passingChecksLength: number, totalChecksLength: number) => {
  if (passingChecksLength === totalChecksLength) {
    return 'exemplary';
  }
  if (passingChecksLength === 0) {
    return 'poor';
  }
  return 'adequate';
};

const getScoreText = (score: string) => {
  if (score === 'exemplary') {
    return t('outcomes_validation_all_criteria_met').toString();
  }
  return t('outcomes_validation_criteria_not_met').toString();
};
